import styled from '@emotion/styled';
import { Paper, PaperProps, useTheme } from '@mui/material';

import Autocomplete, {
    AutocompleteChangeReason,
    AutocompleteRenderGroupParams,
    AutocompleteRenderInputParams,
} from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

import arrowDown from '@/images/ArrowDown.svg';
import checkedBox from '@/images/Checkbox.svg';
import React, { useState } from 'react';

import Button from '../Buttons/Button';
import { MaskedIcon } from '../MaskedIcon/MaskedIcon';
import CustomTooltip from '../ToolTip/CustomTooltip';

const OptionContent = styled.div`
    display: flex;
    justify-content: space-between;
    flex-grow: 1;
    margin-left: 1rem;
`;

const OptionLabelWrapper = styled.div`
    display: flex;
    gap: 5px;
`;

const StyledBox = styled(Box, { shouldForwardProp: prop => !['isSelected', 'isDisabled'].includes(prop) })<{
    isSelected?: boolean;
    isDisabled?: boolean;
}>`
    & > img {
        margin-right: 2rem;
        flex-shrink: 0;
    }
    height: 50px;
    background-color: ${({ isSelected, theme }) => (isSelected ? `${theme.colors.black[100]} !important` : 'none')};
    opacity: ${({ isDisabled }) => (isDisabled ? 0.5 : 1)};
    pointer-events: ${({ isDisabled }) => (isDisabled ? 'none' : 'auto')};
`;

const OptionBox = React.forwardRef((props: any, ref: React.ForwardedRef<any>) => {
    return <StyledBox ref={ref} component="li" {...props} />;
});

function renderOption<T>(
    props: React.HTMLAttributes<HTMLLIElement>,
    option: T,
    values: T[],
    getOptionLabel?: (option: T) => string,
    renderOptionValue?: (option: T) => React.ReactNode,
    useDisabledOptions: boolean = false,
    disabledItemTooltipContent?: React.ReactNode,
    disabledItemTooltipStyles?: any
) {
    const isSelected = Boolean(values?.includes(option));
    const isDisabled = useDisabledOptions && !isSelected;
    const optionBox = (
        <OptionBox {...props} key={getOptionLabel?.(option)} isSelected={isSelected} isDisabled={isDisabled}>
            <OptionContent>
                {renderOptionValue?.(option) || <OptionLabelWrapper>{getOptionLabel?.(option)}</OptionLabelWrapper>}
                <CheckedBox checked={isSelected} />
            </OptionContent>
        </OptionBox>
    );
    if (isDisabled && disabledItemTooltipContent) {
        return (
            <CustomTooltip
                slotProps={disabledItemTooltipStyles}
                title={disabledItemTooltipContent}
                placement="bottom-start"
                containerWidth="100%"
                sx={{ marginBottom: '5px' }}>
                {optionBox}
            </CustomTooltip>
        );
    }
    return optionBox;
}

const renderInput = (params: AutocompleteRenderInputParams, placeholder: string, id: string) => {
    return (
        <TextField
            {...params}
            id={`${id}-input`}
            placeholder={placeholder}
            InputProps={{
                ...params.InputProps,
                sx: {
                    gap: '10px',
                    fontSize: '14px',
                    flexWrap: 'wrap',
                    input: {
                        cursor: 'pointer',
                    },
                },
            }}
        />
    );
};

const CAutoComplete = styled(Autocomplete)`
    & .MuiInputBase-root {
        border-radius: 8px;
        padding-left: 2rem;
        flex-wrap: wrap;
    }

    & .Mui-disabled .MuiOutlinedInput-notchedOutline {
        border-color: #e6e6e6 !important;
    }

    & label.Mui-focused {
        color: ${({ theme }) => theme.colors?.secondary.main} !important;
    }

    & .MuiAutocomplete-inputRoot {
        &.Mui-focused .MuiOutlinedInput-notchedOutline {
            border: 1px solid ${({ theme }) => theme.colors?.secondary.main};
        }
    }

    .MuiAutocomplete-endAdornment {
        margin-top: 25px !important;
    }
` as typeof Autocomplete;

const Group = styled.div`
    font-size: 1.25rem;
    padding: 0.3rem 0 0.6rem 1.25rem;
`;

const PaperButtons = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 8px 20px;
    align-items: center;
    border-radius: 0 0 8px 8px;
    border-top: 1px solid ${({ theme }) => theme.colors?.secondary[300]};
    background: #fff;
`;

interface PaperAdditionalProps {
    onClose: () => void;
    onClearAll: () => void;
    disableClear: boolean;
}

const CustomPaper = (props: PaperProps<'div', PaperAdditionalProps>) => {
    const { onClose, onClearAll, disableClear, ...rest } = props;
    return (
        <Paper elevation={8} sx={{ marginBottom: '1rem' }} {...rest}>
            {props.children}
            <PaperButtons>
                <Button
                    color="secondary"
                    variant="text"
                    disabled={disableClear}
                    onMouseDown={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                        event.preventDefault();
                        onClearAll();
                    }}>
                    Clear all
                </Button>
                <Button
                    color="secondary"
                    variant="contained"
                    className="multiselect__done"
                    onMouseDown={(event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
                        event.preventDefault();
                        onClose();
                    }}>
                    Done
                </Button>
            </PaperButtons>
        </Paper>
    );
};

interface Props<T> {
    id: string;
    placeholder: string;
    label: string;
    options: T[];
    getOptionLabel?: (option: T) => string;
    className?: string;
    onChange?: (event: React.SyntheticEvent, value: T[] | null, reason: AutocompleteChangeReason) => void;
    disabled?: boolean;
    values?: T[];
    renderChip: (option: T, tagProps: object) => React.ReactNode;
    renderOptionValue?: (option: T) => React.ReactNode;
    noOptionsText?: React.ReactNode;
    onClearAll: () => void;
    useDisabledOptions?: boolean;
    disabledItemTooltipContent?: React.ReactNode;
    disabledItemTooltipStyles?: any;
    testId?: string;
}

/**
 * A select with adornment
 */
export default function MultiSelect<T>({
    id,
    values = [],
    placeholder,
    options,
    onChange,
    getOptionLabel,
    disabled,
    className,
    renderChip,
    renderOptionValue,
    noOptionsText,
    onClearAll,
    useDisabledOptions = false,
    disabledItemTooltipStyles = {},
    disabledItemTooltipContent,
    testId,
}: Props<T>) {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    return (
        <CAutoComplete
            open={dropdownOpen}
            onOpen={() => setDropdownOpen(true)}
            onClose={() => setDropdownOpen(false)}
            multiple
            clearOnEscape
            disableClearable
            id={id}
            data-testid={testId}
            className={className}
            options={options}
            autoHighlight
            getOptionLabel={getOptionLabel}
            value={values}
            noOptionsText={noOptionsText}
            slotProps={{
                paper: {
                    component: CustomPaper,
                    disableClear: !values.length,
                    onClose: () => setDropdownOpen(false),
                    onClearAll,
                } as Partial<PaperProps<'div', PaperAdditionalProps>>,
                popper: { placement: 'top-start' },
            }}
            onChange={onChange}
            disableCloseOnSelect
            popupIcon={<MaskedIcon src={arrowDown} size="24px" />}
            renderTags={(value: readonly T[], getTagProps) =>
                value.map((option: T, index: number) => renderChip(option, getTagProps({ index })))
            }
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: T) =>
                renderOption<T>(
                    props,
                    option,
                    values,
                    getOptionLabel,
                    renderOptionValue,
                    useDisabledOptions,
                    disabledItemTooltipContent,
                    disabledItemTooltipStyles
                )
            }
            renderGroup={(params: AutocompleteRenderGroupParams) => (
                <div key={params.key}>
                    <Group>{params.group}</Group>
                    {params.children}
                </div>
            )}
            renderInput={(params: AutocompleteRenderInputParams) => renderInput(params, placeholder, id)}
            disabled={disabled}
        />
    );
}

const EmptyBox = styled.span`
    height: 20px;
    width: 20px;
    border-radius: 4px;
    border: 1px solid var(--secondary-shades-500, #82807e);
`;
function CheckedBox({ checked }: { checked: boolean }) {
    const theme = useTheme();
    return checked ? <MaskedIcon src={checkedBox} size="20px" color={theme.colors.secondary.main} /> : <EmptyBox />;
}
